/**
 * Decision card — full read-only detail for one agent decision.
 *
 * Reads GET /api/agentic/decisions/{id} (backend/app/routes/agentic_decisions.py):
 * the decision, its append-only human/timer response history, supervised
 * deadline state, execution outcomes, and the full reproducibility version
 * tuple. Strictly read-only; no approve/reject actions in this slice.
 */
import { useParams, Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Heading,
  Icon,
  PageHeader,
  Section,
  Spinner,
  Stack,
  Text,
} from '@smms/design-system';
import { getDecision, type RiskClassification } from '../api/agentic';
import { isNotEntitled } from '../api/client';
import NotEnabled from '../components/NotEnabled';

const RISK_BADGE: Record<RiskClassification, 'success' | 'warning' | 'danger'> = {
  routine: 'success',
  non_routine: 'warning',
  safety_critical: 'danger',
};

const RISK_LABEL: Record<RiskClassification, string> = {
  routine: 'Routine',
  non_routine: 'Non-routine',
  safety_critical: 'Safety-critical',
};

function humanize(s: string): string {
  return s.replaceAll('_', ' ');
}

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function JsonBlock({ value, label }: { value: Record<string, unknown> | null; label: string }) {
  if (!value || Object.keys(value).length === 0) {
    return (
      <Text variant="body-sm" tone="muted">
        —
      </Text>
    );
  }
  return (
    <pre
      className="overflow-x-auto rounded-md bg-elevated p-3 text-xs text-text"
      // WCAG 2.1.1 (axe scrollable-region-focusable): `overflow-x-auto`
      // makes this horizontally scrollable once the JSON is wide, but a
      // bare <pre> is not natively focusable — without tabIndex, keyboard
      // users have no way to scroll it (only mouse/trackpad drag works).
      // Same documented pattern as Modal.tsx / Drawer.tsx's scrollable
      // regions and frontend/DiagnosticPage.tsx's JSON output blocks.
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      aria-label={label}
    >
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

export default function DecisionDetailPage() {
  const { id = '' } = useParams();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['agentic', 'decision', id],
    queryFn: () => getDecision(id),
    enabled: Boolean(id),
  });

  const backLink = (
    <Link
      to="/decisions"
      className="inline-flex items-center gap-1 text-sm text-muted transition-colors hover:text-text"
    >
      <Icon as={ArrowLeft} size="sm" />
      Back to Decision Inbox
    </Link>
  );

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="decision-loading">
        <Spinner size="lg" label="Loading decision" />
      </div>
    );
  }

  if (error) {
    if (isNotEntitled(error)) return <NotEnabled />;
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-8">
        <EmptyState
          variant="error"
          icon={<Icon as={AlertTriangle} size="lg" />}
          title="Couldn't load this decision"
          description="It may not exist for your tenant, or something went wrong. Try again."
          action={<Button onClick={() => void refetch()}>Retry</Button>}
        />
      </div>
    );
  }

  if (!data) return <NotEnabled />;

  return (
    <div className="mx-auto w-full max-w-4xl p-6">
      <Stack gap="lg">
        {backLink}

        <PageHeader
          title={humanize(data.decision_type)}
          description={`${humanize(data.agent_id)} · ${data.autonomy_phase} · ${formatWhen(
            data.created_at,
          )}`}
          actions={
            <div className="flex items-center gap-2">
              <Badge variant={RISK_BADGE[data.risk_classification]}>
                {RISK_LABEL[data.risk_classification]}
              </Badge>
              <Badge variant="info">
                {data.calibrated_confidence ?? data.raw_confidence}% confidence
              </Badge>
            </div>
          }
        />

        <Section>
          <Card padding="md">
            <Stack gap="sm">
              <Heading level={2} className="text-base">
                Recommended action
              </Heading>
              <JsonBlock value={data.action_recommended} label="Recommended action (raw JSON)" />
            </Stack>
          </Card>
        </Section>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card padding="md">
            <Stack gap="sm">
              <Heading level={2} className="text-base">
                Reasoning
              </Heading>
              <JsonBlock value={data.reasoning_trace} label="Reasoning trace (raw JSON)" />
            </Stack>
          </Card>
          <Card padding="md">
            <Stack gap="sm">
              <Heading level={2} className="text-base">
                Evidence
              </Heading>
              <JsonBlock value={data.evidence_refs} label="Evidence references (raw JSON)" />
            </Stack>
          </Card>
        </div>

        {data.deadline && (
          <Section>
            <Card padding="md">
              <Stack gap="sm">
                <div className="flex items-center gap-2">
                  <Heading level={2} className="text-base">
                    Supervision
                  </Heading>
                  <Badge
                    variant={data.deadline.state === 'escalated' ? 'danger' : 'warning'}
                    size="sm"
                  >
                    {humanize(data.deadline.state)}
                  </Badge>
                </div>
                <Text variant="body-sm" tone="muted">
                  Delivery confirmed {formatWhen(data.deadline.delivery_confirmed_at)} · override by{' '}
                  {formatWhen(data.deadline.override_deadline)} · max-hold{' '}
                  {formatWhen(data.deadline.max_hold_deadline)}
                  {data.deadline.escalated_at
                    ? ` · escalated ${formatWhen(data.deadline.escalated_at)}`
                    : ''}
                </Text>
              </Stack>
            </Card>
          </Section>
        )}

        <Section>
          <Stack gap="sm">
            <Heading level={2} className="text-base">
              Response history
            </Heading>
            {data.responses.length === 0 ? (
              <Card padding="md">
                <Text variant="body-sm" tone="muted">
                  No human or timer response recorded yet.
                </Text>
              </Card>
            ) : (
              <Card padding="none">
                <ul className="divide-y divide-border">
                  {data.responses.map(r => (
                    <li key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                      <Badge variant="info" size="sm">
                        {humanize(r.response_type)}
                      </Badge>
                      <Text variant="caption" tone="muted" className="flex-1">
                        {r.response_source}
                        {r.response_role ? ` · ${humanize(r.response_role)}` : ''} ·{' '}
                        {formatWhen(r.created_at)}
                      </Text>
                      {r.passive_flag && (
                        <Badge variant="warning" size="sm">
                          passive
                        </Badge>
                      )}
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </Stack>
        </Section>

        {data.outcomes.length > 0 && (
          <Section>
            <Card padding="md">
              <Stack gap="sm">
                <Heading level={2} className="text-base">
                  Execution outcome
                </Heading>
                {data.outcomes.map((o, i) => (
                  <JsonBlock key={i} value={o.execution_result} label="Execution outcome (raw JSON)" />
                ))}
              </Stack>
            </Card>
          </Section>
        )}

        <Section>
          <Card padding="md">
            <Stack gap="sm">
              <Heading level={2} className="text-base">
                Reproducibility
              </Heading>
              <dl className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
                {Object.entries(data.versions).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4">
                    <Text as="dt" variant="caption" tone="muted">
                      {humanize(k)}
                    </Text>
                    <Text as="dd" variant="mono" className="text-xs">
                      {v ?? '—'}
                    </Text>
                  </div>
                ))}
                <div className="flex justify-between gap-4">
                  <Text as="dt" variant="caption" tone="muted">
                    idempotency key
                  </Text>
                  <Text as="dd" variant="mono" className="truncate text-xs">
                    {data.idempotency_key}
                  </Text>
                </div>
              </dl>
            </Stack>
          </Card>
        </Section>
      </Stack>
    </div>
  );
}
